import { entities, formatters, notUndefinedOrNull, plainToInstance, utilities } from "@fraction/shared";
import { parseISO } from "date-fns";
import _ from "lodash";
import { HelpCircleIcon, Loader } from "lucide-react";
import { FocusEvent, forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { toast } from "react-toastify";
import Skeleton from "src/components/Skeleton";
import { Input, InputProps } from "src/components/TextInput/Input";
import Tooltip from "src/components/Tooltip";
import { useApplication } from "src/hooks/useApplication";
import { Logger } from "src/log";
import { cn } from "src/utilities/shadcnUtils";

const logger = new Logger("ModifiableKeyValue");

export interface ModifiableKeyValueHandle {
  refreshValue: () => void;
  setValue: (val: string) => void;
}

export const ModifiableKeyValue = forwardRef<
  ModifiableKeyValueHandle,
  {
    label: string;
    loading?: boolean;
    appId?: string;
    /**
     * The path to the value to be modified.
     * If it is an array, we will return the first non-undefined value and on update, update all values in the paths array.
     */
    path: string | string[];
    formatValue?: (val: any) => string;
    mutateValueBeforeUpdate?: (val: string, prev: any) => any;
    updateOtherApplicationValuesOnUpdate?: (val: entities.ApplicationT) => Record<string, any>;
    cacheUpdateFnProperty?: (
      prev: entities.Property | undefined | null,
      changes: entities.Property
    ) => entities.Property | null | undefined;
    className?: string;
    inputClassName?: string;
    tooltip?: string;
    minDate?: Date;
    rightContent?: React.ReactNode;
    sublabel?: React.ReactNode;
    toastOnMinOrMax?: boolean;
  } & InputProps
>(
  (
    {
      label,
      loading,
      appId,
      path: paths_,
      currency,
      percentage,
      integer,
      cents,
      type,
      formatValue,
      mutateValueBeforeUpdate,
      updateOtherApplicationValuesOnUpdate,
      cacheUpdateFnProperty,
      className,
      inputClassName,
      tooltip,
      minDate,
      rightContent,
      sublabel,
      max,
      min,
      toastOnMinOrMax = true,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const {
      updateApplication,
      updateApplicationProperty,
      upsertApplicationSolicitor,
      data: application,
      isPending,
    } = useApplication({
      id: appId,
      initialRefetch: false,
      cached: true,
    });

    const paths = Array.isArray(paths_) ? paths_ : [paths_];
    const value = paths.map((path) => _.get(application, path)).find(notUndefinedOrNull);

    useImperativeHandle(
      ref,
      () => ({
        refreshValue: () => {
          if (inputRef.current) {
            let fmtValue = value;
            if (currency || cents) {
              fmtValue = formatters.number.getCurrencyFromNumber(fmtValue);
            }
            if (formatValue && value) {
              fmtValue = formatValue(value);
            }
            inputRef.current.value = typeof fmtValue === "string" ? fmtValue : fmtValue?.toString() ?? "";
          }
        },
        setValue: (val: string) => {
          if (inputRef.current) {
            inputRef.current.value = val;
          }
        },
      }),
      [value, currency, cents, formatValue]
    );

    const fmtStr = useCallback(
      (val: string | number) => {
        if (val === "") {
          return "";
        } else if (currency || cents) {
          return formatters.number.getCurrencyFromNumber(val, { inputInDollars: true });
        } else if (percentage) {
          return formatters.number.getPercentageFromDecimal(formatters.number.getNumberFromString(val) / 100);
        } else if (integer) {
          return Math.round(formatters.number.getNumberFromString(val));
        } else {
          return val;
        }
      },
      [currency, cents, percentage, integer]
    );

    const fmt = useCallback(
      (val: string | number) => {
        if (val === "") {
          return null;
        } else if (currency || cents) {
          return formatters.number.getCents(val);
        } else if (percentage) {
          return formatters.number.getNumberFromString(val) / 100;
        } else if (integer) {
          return Math.round(formatters.number.getNumberFromString(val));
        } else {
          return val;
        }
      },
      [currency, cents, percentage, integer]
    );

    const handleBlur = useCallback(
      _.debounce(
        (evt: FocusEvent<HTMLInputElement>) => {
          let newValue: string | number = evt.target.value;

          if (mutateValueBeforeUpdate || max !== undefined || min !== undefined) {
            if (mutateValueBeforeUpdate) {
              newValue = mutateValueBeforeUpdate(newValue, value);
            }
            const oldNumberValue = Number(newValue);
            if (max !== undefined) {
              newValue = Math.min(
                Number(newValue),
                currency || cents ? formatters.number.getDollars(max) : Number(max)
              );
              if (toastOnMinOrMax && oldNumberValue !== Number(newValue)) {
                toast.info(
                  `The maximum allowed value is: ${fmtStr?.(newValue) ?? newValue}, setting to that.`
                );
              }
            }

            if (min !== undefined) {
              newValue = Math.max(
                Number(newValue),
                currency || cents ? formatters.number.getDollars(min) : Number(min)
              );
              if (toastOnMinOrMax && oldNumberValue !== Number(newValue)) {
                toast.info(
                  `The minimum allowed value is: ${fmtStr?.(newValue) ?? newValue}, setting to that.`
                );
              }
            }
            if (inputRef.current) {
              inputRef.current.value =
                typeof newValue === "string"
                  ? newValue
                  : formatValue
                  ? formatValue(newValue)
                  : newValue.toString();
            }
          }

          if (type === "date" && typeof newValue === "string") {
            // we will receive a string in the local timezone, so we want to create a date object
            // with that string, set it to noon of that day to hopefully not have things move from one timezone to the next,
            // and then convert it to UTC
            newValue = utilities.noonOfDay(parseISO(newValue)).toISOString();
          }

          const valueToSet: string | number | null = fmt(newValue);

          if (valueToSet === value) {
            logger.log(`Value not changed for ${label}: ${valueToSet}`);
            return;
          }

          const propertyChanges: Record<string, any> = {};
          const solicitorChanges: Record<string, any> = {};
          const applicationChanges: Record<string, any> = {};

          for (const path of paths) {
            if (path.startsWith("property.")) {
              logger.log(`Setting property ${path}: ${newValue}`);
              const withoutPropertyPrefix = path.replace("property.", "");
              propertyChanges[withoutPropertyPrefix] = valueToSet;
            } else if (path.startsWith("solicitor.")) {
              const withoutPrefix = path.replace("solicitor.", "");
              logger.log(`Setting solicitor ${path} (${withoutPrefix}): ${newValue}`);
              solicitorChanges[withoutPrefix] = valueToSet;
            } else {
              logger.log(`Setting application ${path}: ${newValue}`);
              applicationChanges[path] = valueToSet;
            }
          }

          const pathsToPick = paths;
          if (updateOtherApplicationValuesOnUpdate) {
            const otherApplicationChanges = updateOtherApplicationValuesOnUpdate({
              ...application,
              ...applicationChanges,
            });
            for (const key of Object.keys(otherApplicationChanges)) {
              pathsToPick.push(key);
            }

            if (otherApplicationChanges) {
              Object.assign(applicationChanges, otherApplicationChanges);
            }
          }

          if (Object.keys(propertyChanges).length) {
            updateApplicationProperty({
              ...propertyChanges,
              // @ts-ignore
              cacheUpdateFn: cacheUpdateFnProperty,
            });
          }
          if (Object.keys(solicitorChanges).length) {
            upsertApplicationSolicitor.mutateAsync(solicitorChanges);
          }
          if (Object.keys(applicationChanges).length) {
            updateApplication(_.pick(plainToInstance(entities.Application, applicationChanges), pathsToPick));
          }
          // dates are not debounced
        },
        type === "date" ? 0 : 1100
      ),
      [
        max,
        min,
        value,
        currency,
        cents,
        percentage,
        integer,
        paths,
        mutateValueBeforeUpdate,
        cacheUpdateFnProperty,
      ]
    );

    let fmtValue = value;
    if (currency || cents) {
      fmtValue = formatters.number.getCurrencyFromNumber(fmtValue);
    }
    if (formatValue && value) {
      fmtValue = formatValue(value);
    }

    return (
      <div
        className={cn("flex flex-row flex-wrap gap-1 items-center justify-between p-3 rounded", className)}
      >
        <div className="flex flex-row mr-2">
          <div>
            <p className="text-sm font-bold flex-1">{label}:</p>
            {sublabel}
          </div>
          {tooltip ? (
            <Tooltip white text={tooltip}>
              <HelpCircleIcon className="ml-1" size={12} />
            </Tooltip>
          ) : null}
        </div>

        <div className="flex flex-row items-center gap-1">
          {loading ? (
            <Skeleton width={160} />
          ) : (
            <div className="flex flex-row max-w-[300px]">
              <div className="relative flex flex-row">
                <Input
                  ref={inputRef}
                  type={type}
                  onChange={handleBlur}
                  currency={currency}
                  phone={type?.startsWith("tel")}
                  percentage={percentage}
                  integer={integer}
                  cents={cents}
                  defaultValue={fmtValue}
                  min={type === "date" ? minDate?.toISOString().split("T")[0] : undefined}
                  className={cn(
                    "text-sm text-right text-ellipsis line-clamp-2 bg-gray-400 py-1 pr-2 pl-1 rounded-md w-[120px] min-w-auto",
                    inputClassName
                  )}
                />
                {isPending ? (
                  <div className="bg-gray-400 rounded absolute left-0.5 bottom-0 flex items-center justify-end h-full">
                    <Loader height={18} className="text-gray-600 animate-spin" />
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {rightContent}
        </div>
      </div>
    );
  }
);
